import { useCallback, useEffect, useRef } from "react";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import { useAudioStore } from "@/features/tutor/store/useAudioStore";
import {
  useNotepadStore,
  selectIsNotepadOpen,
} from "@/features/notepad/store/useNotepadStore";
import { debugLog } from "@/utils/debugLog";

export const useInactivityTimer = ({ suppressInactivity = false } = {}) => {
  const inactivityThreshold = 300000;
  const { emit, inactivityRef } = useRuntimeContext();
  const isNotepadOpen = useNotepadStore(selectIsNotepadOpen);
  const notepadIngressType = useNotepadStore((s) => s.ingressType);
  const wasNotepadOpenRef = useRef(isNotepadOpen);
  const suppressInactivityRef = useRef(suppressInactivity);
  // Tracks whether canvas agent is currently speaking (turn-start → turn-done)
  const isCanvasTalkingRef = useRef(false);

  const endInactivityTimer = useCallback(() => {
    if (inactivityRef.current) {
      debugLog("clearing inactivity timer");
      clearTimeout(inactivityRef.current);
      inactivityRef.current = null;
    }
  }, [inactivityRef]);

  const startInactivityTimer = useCallback(() => {
    if (suppressInactivityRef.current) {
      endInactivityTimer();
      return;
    }

    const notepadState = useNotepadStore.getState();
    if (
      selectIsNotepadOpen(notepadState) &&
      notepadState.ingressType === "student"
    ) {
      endInactivityTimer();
      return;
    }

    // Don't start timer if AI is currently speaking (tutor or canvas)
    const isAITalking = useAudioStore.getState().isAITalking;
    if (isAITalking || isCanvasTalkingRef.current) {
      debugLog("AI is speaking, not starting inactivity timer");
      return;
    }

    if (inactivityRef.current) {
      debugLog("clearing inactivity timer");
      clearTimeout(inactivityRef.current);
    }
    inactivityRef.current = setTimeout(() => {
      emit("USER_INACTIVITY");
    }, inactivityThreshold);
  }, [emit, endInactivityTimer, inactivityRef, inactivityThreshold]);

  const resetAndRestartInactivityTimer = useCallback(() => {
    debugLog("resetting and restarting inactivity timer");

    if (suppressInactivityRef.current) {
      endInactivityTimer();
      return;
    }

    const notepadState = useNotepadStore.getState();
    if (
      selectIsNotepadOpen(notepadState) &&
      notepadState.ingressType === "student"
    ) {
      endInactivityTimer();
      return;
    }

    // Don't restart timer if AI is currently speaking (tutor or canvas)
    const isAITalking = useAudioStore.getState().isAITalking;
    if (isAITalking || isCanvasTalkingRef.current) {
      debugLog("AI is speaking, not restarting inactivity timer");
      return;
    }

    if (inactivityRef.current) {
      clearTimeout(inactivityRef.current);
      inactivityRef.current = null;
    }
    // Restart timer from scratch (first timeout: 30s)
    startInactivityTimer();
  }, [endInactivityTimer, inactivityRef, startInactivityTimer]);

  useEffect(() => {
    suppressInactivityRef.current = suppressInactivity;
    if (suppressInactivity) {
      endInactivityTimer();
    }
  }, [endInactivityTimer, suppressInactivity]);

  // Track canvas talking state via window events — block timer while canvas speaks,
  // restart it when canvas audio finishes playing (not just when CANVAS_DONE arrives,
  // since audio chunks arrive after CANVAS_DONE and take time to drain from the buffer).
  useEffect(() => {
    const onTurnStart = () => {
      isCanvasTalkingRef.current = true;
      useAudioStore.getState().setIsAITalking(true);
      endInactivityTimer();
    };
    const onTurnDone = () => {
      isCanvasTalkingRef.current = false;
      // Keep isAITalking=true until audio finishes draining (canvas:audio-playback-ended).
      // Audio chunks arrive after CANVAS_DONE and still need to play out.
    };
    const onAudioPlaybackEnded = () => {
      // Canvas audio has fully played out — clear talking flag and start inactivity timer
      useAudioStore.getState().setIsAITalking(false);
      const notepadState = useNotepadStore.getState();
      if (
        selectIsNotepadOpen(notepadState) &&
        notepadState.ingressType === "ai"
      ) {
        startInactivityTimer();
      }
    };
    window.addEventListener("canvas:turn-start", onTurnStart);
    window.addEventListener("canvas:turn-done", onTurnDone);
    window.addEventListener(
      "canvas:audio-playback-ended",
      onAudioPlaybackEnded,
    );
    return () => {
      window.removeEventListener("canvas:turn-start", onTurnStart);
      window.removeEventListener("canvas:turn-done", onTurnDone);
      window.removeEventListener(
        "canvas:audio-playback-ended",
        onAudioPlaybackEnded,
      );
    };
  }, [endInactivityTimer, startInactivityTimer]);

  useEffect(() => {
    // Only disable inactivity timer for student review mode, not AI mode
    if (isNotepadOpen && notepadIngressType === "student") {
      endInactivityTimer();
    } else if (isNotepadOpen && notepadIngressType === "ai") {
      // Canvas opened in AI mode — do NOT start timer here; the canvas agent
      // will speak first (initialization response), and canvas:turn-done will
      // start the timer once the first response finishes.
      endInactivityTimer();
    } else if (wasNotepadOpenRef.current && !isNotepadOpen) {
      startInactivityTimer();
    }
    wasNotepadOpenRef.current = isNotepadOpen;
  }, [
    endInactivityTimer,
    isNotepadOpen,
    notepadIngressType,
    startInactivityTimer,
  ]);

  return {
    inactivityThreshold,
    startInactivityTimer,
    endInactivityTimer,
    resetAndRestartInactivityTimer,
  };
};
